/**
 * Gate / eligibility (PRD §2.2, §2.3, §3 gate, §6 spacing, §9 loading, §9.1 page types).
 */
import { LOCKED } from './config.js';

/**
 * §9.1 / §2.3: is this a page that may carry the player at all?
 * @param {object} input
 * @param {Window} input.win
 * @param {Document} input.doc
 * @param {object} input.config
 * @returns {{eligible:boolean, reason:string}}
 */
export function checkPage({ win, doc, config }) {
  if (!config.enabled) return { eligible: false, reason: 'disabled' }; // C-OFF / kill switch
  let framed = true;
  try {
    framed = win.top !== win; // §9.1: embedded in someone else's iframe -> off
  } catch (error) {
    framed = true;
  }
  if (framed) return { eligible: false, reason: 'framed' };
  const html = doc.documentElement;
  if (html.hasAttribute('amp') || html.hasAttribute('⚡')) return { eligible: false, reason: 'amp' };
  if (!doc.querySelector(config.articleSelector)) return { eligible: false, reason: 'no_article' };
  if (!doc.querySelector('.qsvp-slot')) return { eligible: false, reason: 'no_slot' };
  return { eligible: true, reason: '' };
}

/**
 * Arbitrage source and campaign (assumption A9: read from the URL, kept for the browser session).
 * @param {object} input
 * @param {string} input.search location.search
 * @param {Storage|null} input.storage sessionStorage
 * @returns {{qsSrc:string, qsCmp:string}}
 */
export function readTrafficSource({ search, storage }) {
  const params = new URLSearchParams(search || '');
  let qsSrc = params.get('qs_src') || '';
  let qsCmp = params.get('qs_cmp') || '';
  try {
    if (qsSrc || qsCmp) {
      if (storage) storage.setItem('qsvp_src', JSON.stringify({ qsSrc, qsCmp }));
    } else if (storage) {
      const saved = JSON.parse(storage.getItem('qsvp_src') || 'null');
      if (saved) ({ qsSrc, qsCmp } = saved);
    }
  } catch (error) {
    // Storage blocked: URL values only.
  }
  return { qsSrc: String(qsSrc).slice(0, 64), qsCmp: String(qsCmp).slice(0, 64) };
}

/**
 * IVT-2: sources listed in traffic.videoExcludeSources get display only.
 * @param {object} config
 * @param {string} qsSrc
 * @returns {boolean}
 */
export function isVideoAllowedForSource(config, qsSrc) {
  return !(qsSrc && (config.traffic.videoExcludeSources || []).includes(qsSrc));
}

/**
 * IVT-1 / PERF-8: resolve on the first genuine interaction. Synthetic events (isTrusted false)
 * never count. The listener only records; the caller's work runs in a later task.
 * @param {object} input
 * @param {Window} input.win
 * @param {Function} input.setTimeoutFn
 * @param {Function} [input.isTrusted] (event) => boolean, injectable for tests
 * @param {boolean} [input.scroll] config.gateOnScroll: a scroll opens the gate too
 * @returns {Promise<string>} the event type
 */
export function waitForInteraction({ win, setTimeoutFn, isTrusted = (event) => event.isTrusted, scroll = false }) {
  // From the 2.0.1 hand edit: no "scroll". Browsers fire trusted scroll events on scroll restoration
  // and anchor jumps, which opened the gate without a real user action (§3 gate).
  // 2.0.8: config.gateOnScroll (default off) adds it back, accepting that IVT risk for earlier starts.
  const types = ['touchstart', 'pointerdown', 'click', 'keydown', 'wheel'].concat(scroll ? ['scroll'] : []);
  return new Promise((resolve) => {
    const options = { passive: true, capture: true };
    const onEvent = (event) => {
      if (!isTrusted(event)) return; // IVT-1
      types.forEach((type) => win.removeEventListener(type, onEvent, options));
      setTimeoutFn(() => resolve(event.type), 0); // PERF-8: build in a later task
    };
    types.forEach((type) => win.addEventListener(type, onEvent, options));
  });
}

/**
 * Count words in an element's text.
 * @param {Element} el
 * @returns {number}
 */
export function countWords(el) {
  if (!el) return 0;
  // Count per text node: textContent glues adjacent paragraphs together ("end.Start").
  const walker = el.ownerDocument.createTreeWalker(el, 4 /* NodeFilter.SHOW_TEXT */);
  let count = 0;
  while (walker.nextNode()) count += (walker.currentNode.nodeValue.match(/\S+/g) || []).length;
  return count;
}

/**
 * Number of <p> elements between two nodes in document order.
 * @param {Element} article
 * @param {Element} first
 * @param {Element} second
 * @returns {number}
 */
export function paragraphsBetween(article, first, second) {
  let count = 0;
  for (const paragraph of article.querySelectorAll('p')) {
    const afterFirst = first.compareDocumentPosition(paragraph) & 4; // DOCUMENT_POSITION_FOLLOWING
    const beforeSecond = second.compareDocumentPosition(paragraph) & 2; // DOCUMENT_POSITION_PRECEDING
    if (afterFirst && beforeSecond) count += 1;
  }
  return count;
}

/**
 * Select which server-rendered slots become players (MP-1, MP-11, C21, §13 players).
 * The script never inserts or moves slots; ignored slots stay as the CMS printed them.
 * @param {object} input
 * @param {Array<{id:string, top:number, el:Element}>} input.slots in document order, top = page Y
 * @param {object} input.config
 * @param {string} input.profileName
 * @param {number} input.viewportHeight
 * @param {Element} input.article
 * @param {number} input.articleWords
 * @param {Function} [input.densityOk] (slotsSoFar) => boolean, mobile C21 check
 * @returns {{selected:string[], skipped:Array<{id:string, reason:string}>}}
 */
export function selectSlots({ slots, config, profileName, viewportHeight, article, articleWords, densityOk = () => true }) {
  const isMobile = profileName === 'phone' || profileName === 'narrow';
  const max = isMobile ? LOCKED.maxPlayersMobile : LOCKED.maxPlayersDesktop;
  const selected = [];
  const skipped = [];
  let previous = null;
  for (const slot of slots) {
    const playerConfig = (config.players || []).find((player) => player.id === slot.id);
    const skip = (reason) => skipped.push({ id: slot.id, reason });
    if (!playerConfig) {
      skip('not_configured');
      continue;
    }
    if (playerConfig.enabled === false) {
      skip('player_disabled');
      continue;
    }
    if (playerConfig.minArticleWords && articleWords < playerConfig.minArticleWords) {
      skip('article_too_short');
      continue;
    }
    if (selected.length >= max) {
      skip('max_players');
      continue;
    }
    if (previous) {
      // §6 spacing: at least one viewport height and minParagraphsBetweenPlayers apart,
      // and never two slots in the first screen.
      if (slot.top - previous.top < viewportHeight) {
        skip('too_close');
        continue;
      }
      if (paragraphsBetween(article, previous.el, slot.el) < config.minParagraphsBetweenPlayers) {
        skip('too_few_paragraphs');
        continue;
      }
      if (isMobile && !densityOk(selected.length + 1)) {
        skip('density'); // C21
        continue;
      }
    }
    selected.push(slot.id);
    previous = slot;
  }
  return { selected, skipped };
}
